/* Twomatoes Quiz Engine v21.
 * Reads Shopify-rendered metaobject data; never stores secrets or submits data.
 * A future secure backend may listen for `twomatoes:quiz:completed` to save a result.
 */
(() => {
  'use strict';

  function readModel(root) {
    const outcomes = Array.from(root.querySelectorAll('[data-quiz-outcome]')).map((node, order) => ({
      key: node.dataset.outcomeKey || '',
      title: node.dataset.outcomeTitle || '',
      description: node.dataset.outcomeDescription || '',
      priority: Number.isFinite(Number(node.dataset.tiePriority)) ? Number(node.dataset.tiePriority) : 999,
      order
    })).filter(item => item.key);

    const questions = Array.from(root.querySelectorAll('[data-quiz-question]')).map((node, order) => {
      const key = node.dataset.questionKey || `question-${order + 1}`;
      const type = node.dataset.questionType === 'multiple' ? 'multiple' : 'single';
      const answers = Array.from(node.querySelectorAll('[data-quiz-answer]')).map((button, index) => ({
        key: button.dataset.answerKey || `answer-${index + 1}`,
        text: (button.querySelector('.twomatoes-quiz__answer-label') || button).textContent.trim(),
        button,
        rules: Array.from(button.querySelectorAll('[data-quiz-rule]')).map(rule => ({
          outcome: rule.dataset.outcomeKey || '', points: Number(rule.dataset.points)
        })).filter(rule => rule.outcome && Number.isFinite(rule.points))
      }));
      return { key, type, autoAdvance: type === 'single' && node.dataset.autoAdvance === 'true', node, answers };
    });

    return { quizId: root.dataset.quizId || '', quizHandle: root.dataset.quizHandle || '', outcomes, questions };
  }

  // Always recalculate from the current answers. Going Back and changing an answer cannot double-count points.
  function scoreQuiz(model, selections) {
    const totals = new Map(model.outcomes.map(outcome => [outcome.key, 0]));
    let appliedRuleCount = 0;
    model.questions.forEach(question => {
      const selected = selections.get(question.key) || new Set();
      question.answers.forEach(answer => {
        if (!selected.has(answer.key)) return;
        answer.rules.forEach(rule => {
          if (totals.has(rule.outcome)) {
            totals.set(rule.outcome, totals.get(rule.outcome) + rule.points);
            appliedRuleCount += 1;
          }
        });
      });
    });
    const ranked = [...model.outcomes].sort((a, b) => {
      const scoreDifference = totals.get(b.key) - totals.get(a.key);
      if (scoreDifference !== 0) return scoreDifference;
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.order - b.order;
    });
    return { totals, winner: ranked[0] || null, appliedRuleCount };
  }

  function applyAnswerTokens(text, model, selections) {
    // Optional personalization in outcome title/description: [[answer:question_key]].
    // Uses visible selected answer text; no HTML is interpreted.
    return String(text || '').replace(/\[\[answer:([a-zA-Z0-9_-]+)\]\]/g, (_match, key) => {
      const question = model.questions.find(item => item.key === key);
      if (!question) return '';
      const chosen = selections.get(key) || new Set();
      return question.answers.filter(answer => chosen.has(answer.key)).map(answer => answer.text).join(', ');
    });
  }

  function initialize(root) {
    if (root.dataset.quizInitialized === 'true') return;
    const questionContainer = root.querySelector('[data-quiz-questions]');
    if (!questionContainer) return;
    root.dataset.quizInitialized = 'true';

    const model = readModel(root);
    const selections = new Map();
    let current = 0;
    let pendingAdvance = null;
    const progress = root.querySelector('[data-quiz-progress]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const progressFill = root.querySelector('[data-quiz-progress-fill]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const progressPercent = root.querySelector('[data-quiz-progress-percent]');
    const controls = root.querySelector('[data-quiz-controls]');
    const previous = root.querySelector('[data-quiz-back]');
    const next = root.querySelector('[data-quiz-next]');
    const result = root.querySelector('[data-quiz-result]');
    const error = root.querySelector('[data-quiz-error]');
    const restart = root.querySelector('[data-quiz-restart]');

    function showError(message) {
      if (error) { error.textContent = message; error.hidden = false; }
    }

    const keys = model.questions.map(question => question.key);
    const uniqueKeys = new Set(keys);
    if (!model.questions.length || !model.outcomes.length || uniqueKeys.size !== keys.length || model.questions.some(question => question.answers.length === 0)) {
      showError('This quiz has an incomplete setup. Check the questions, answers, and outcomes in Shopify Admin. Question keys must be unique.');
      questionContainer.hidden = true;
      if (progress) progress.hidden = true;
      if (controls) controls.hidden = true;
      return;
    }

    function selectedFor(question) { return selections.get(question.key) || new Set(); }

    function refreshChoices(question) {
      const selected = selectedFor(question);
      question.answers.forEach(answer => answer.button.setAttribute('aria-pressed', String(selected.has(answer.key))));
      next.disabled = selected.size === 0;
    }

    function showQuestion(index, focus = false) {
      if (pendingAdvance !== null) { clearTimeout(pendingAdvance); pendingAdvance = null; }
      current = Math.max(0, Math.min(model.questions.length - 1, index));
      result.hidden = true;
      questionContainer.hidden = false;
      progress.hidden = false;
      controls.hidden = false;
      model.questions.forEach((question, i) => { question.node.hidden = i !== current; });
      const question = model.questions[current];
      const percent = Math.round((current / model.questions.length) * 100);
      progressLabel.textContent = `Question ${current + 1} of ${model.questions.length}`;
      progressPercent.textContent = `${percent}%`;
      progressFill.style.width = `${percent}%`;
      progressBar.setAttribute('aria-valuenow', String(percent));
      previous.hidden = current === 0;
      next.textContent = current === model.questions.length - 1 ? 'See my result →' : 'Next →';
      refreshChoices(question);
      if (focus) question.node.querySelector('h2')?.focus({ preventScroll: true });
    }

    function completedPayload(scored) {
      return {
        schemaVersion: 1,
        quizId: model.quizId,
        quizHandle: model.quizHandle,
        completedAt: new Date().toISOString(),
        responses: model.questions.map(question => ({
          questionKey: question.key,
          answerKeys: [...selectedFor(question)]
        })),
        scores: model.outcomes.map(outcome => ({ outcomeKey: outcome.key, points: scored.totals.get(outcome.key) })),
        resultKey: scored.winner?.key || null
      };
    }

    function finish() {
      const missing = model.questions.find(question => selectedFor(question).size === 0);
      if (missing) { showError('Please answer every question before viewing your result.'); showQuestion(model.questions.indexOf(missing), true); return; }
      const scored = scoreQuiz(model, selections);
      if (!scored.winner || scored.appliedRuleCount === 0) {
        showError('This quiz does not yet have a scoring result. Assign a scoring rule to at least one selected answer.');
        return;
      }
      if (error) error.hidden = true;
      questionContainer.hidden = true;
      progress.hidden = true;
      controls.hidden = true;
      result.hidden = false;
      const title = root.querySelector('[data-quiz-result-title]');
      title.textContent = applyAnswerTokens(scored.winner.title, model, selections);
      root.querySelector('[data-quiz-result-description]').textContent = applyAnswerTokens(scored.winner.description, model, selections);
      const scoreList = root.querySelector('[data-quiz-score-list]');
      scoreList.replaceChildren();
      model.outcomes.forEach(outcome => {
        const row = document.createElement('li');
        row.className = 'twomatoes-quiz__score-row body-s-400-left';
        row.dataset.winner = String(outcome.key === scored.winner.key);
        const label = document.createElement('span');
        label.textContent = outcome.title;
        const points = document.createElement('strong');
        points.textContent = `${scored.totals.get(outcome.key)} pts`;
        row.append(label, points);
        scoreList.append(row);
      });
      title.focus({ preventScroll: true });
      // A notification for a future secure persistence integration; not a claim that data was stored.
      root.dispatchEvent(new CustomEvent('twomatoes:quiz:completed', { bubbles: true, detail: completedPayload(scored) }));
    }

    model.questions.forEach((question, index) => {
      question.answers.forEach(answer => answer.button.addEventListener('click', () => {
        if (pendingAdvance !== null) { clearTimeout(pendingAdvance); pendingAdvance = null; }
        const selected = new Set(selectedFor(question));
        if (question.type === 'multiple') {
          if (selected.has(answer.key)) selected.delete(answer.key);
          else selected.add(answer.key);
        } else {
          selected.clear();
          selected.add(answer.key);
        }
        selections.set(question.key, selected);
        refreshChoices(question);
        if (error) error.hidden = true;
        if (question.autoAdvance && selected.size && current === index) {
          pendingAdvance = setTimeout(() => {
            pendingAdvance = null;
            if (current !== index) return;
            if (current < model.questions.length - 1) showQuestion(current + 1, true);
            else finish();
          }, 200);
        }
      }));
    });

    previous.addEventListener('click', () => showQuestion(current - 1, true));
    next.addEventListener('click', () => {
      if (!selectedFor(model.questions[current]).size) return;
      if (current < model.questions.length - 1) showQuestion(current + 1, true);
      else finish();
    });
    restart.addEventListener('click', () => {
      selections.clear();
      if (error) error.hidden = true;
      showQuestion(0, true);
    });
    showQuestion(0);
  }

  function initializeAll(scope) {
    if (scope.matches?.('[data-twomatoes-quiz]')) initialize(scope);
    scope.querySelectorAll?.('[data-twomatoes-quiz]').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  else initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
})();
